<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { higherRateClaimablePence } from '@/helpers/claimTaxBack/higherRateClaim'
import { V1_CLAIMABLE_PERIOD_KEY } from '@/helpers/claimTaxBack/taxYearConfig'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { useDashboardStatementShortcut } from '@/composables/useDashboardStatementShortcut'
import debug from '@/helpers/debug'

/**
 * Statement shortcut under notifications. The year uses the same section
 * The card is the statement. Only "View statement" and "Check for free"
 * are links. The claim note sits inside the card, not on its edge.
 *
 * Statements load after the rest of the home page. A placeholder holds the
 * slot so the card can ease in instead of pushing the page down.
 */
const { latestStatement, taxYearLabel, isLoading } = useDashboardStatementShortcut()
const { hasCompletedChecker, isClaimAvailable, isReady } = useClaimTaxBack()

const claimAmount = computed(() => {
  const statement = latestStatement.value
  // A 404 leaves the lookup ready with no profile. The statement card stays;
  // the claim footer needs the same availability gate as the sidebar.
  if (!isReady.value || !isClaimAvailable.value || hasCompletedChecker.value) {
    return ''
  }
  if (!statement || statement.period !== V1_CLAIMABLE_PERIOD_KEY) return ''
  const pence = higherRateClaimablePence(
    statement.totalDonated,
    claimTaxYearConfig
  )
  return pence == null ? '' : formatMoneyFromPence(pence)
})

// True only when this visit had to wait for the statement. A cached
// statement paints in place, with no entrance.
const arriving = ref(false)
const footerOpen = ref(!isLoading.value && Boolean(claimAmount.value))
const retainWhileClosing = ref(false)
const slotOpen = ref(isLoading.value || Boolean(latestStatement.value))
let closeTimer = 0

const showSection = computed(
  () =>
    isLoading.value ||
    Boolean(latestStatement.value) ||
    retainWhileClosing.value
)

onUnmounted(() => window.clearTimeout(closeTimer))

watch(isLoading, (loading, wasLoading) => {
  if (loading) {
    retainWhileClosing.value = false
    slotOpen.value = true
    return
  }
  if (!wasLoading) return
  if (latestStatement.value) {
    arriving.value = true
    slotOpen.value = true
    debug('[quick-actions] statement arrived', {
      period: latestStatement.value.period,
    })
    return
  }
  slotOpen.value = false
  retainWhileClosing.value = true
  window.clearTimeout(closeTimer)
  closeTimer = window.setTimeout(() => {
    retainWhileClosing.value = false
  }, 480)
})

// A later fetch can fill statements after a failed one closed the slot.
// Loading does not change then, so open the slot here with the same entrance.
watch(latestStatement, (statement, previous) => {
  if (!statement || previous || isLoading.value || slotOpen.value) return
  window.clearTimeout(closeTimer)
  retainWhileClosing.value = false
  arriving.value = true
  slotOpen.value = true
  debug('[quick-actions] statement arrived late', {
    period: statement.period,
  })
})

watch(claimAmount, (amount, previous) => {
  debug('[quick-actions] higher rate footer', { amount: amount || null })
  if (!amount) {
    footerOpen.value = false
    return
  }
  // Grow the footer when it was not already on screen.
  if (previous === '' || arriving.value) {
    footerOpen.value = false
    requestAnimationFrame(() => {
      footerOpen.value = Boolean(claimAmount.value)
    })
    return
  }
  footerOpen.value = true
})
</script>

<template>
  <section
    v-if="showSection"
    class="sa-quick-actions"
    :class="{
      'is-open': slotOpen,
      'is-arriving': arriving && Boolean(latestStatement),
    }"
    :aria-busy="isLoading ? 'true' : undefined"
    :aria-labelledby="
      !isLoading && latestStatement ? 'dashboard-statement-heading' : undefined
    "
    data-testid="dashboard-quick-actions"
  >
    <div class="sa-quick-actions__slot">
      <div class="sa-quick-actions__slot-clip">
        <div
          v-if="isLoading"
          class="sa-quick-actions__pending"
          data-testid="dashboard-statement-pending"
        >
          <p class="sa-sr-only">
            {{ $t('Dashboard.Home.QuickActions.Loading') }}
          </p>
          <div class="sa-quick-actions__pending-title sa-skeleton" />
          <div class="sa-quick-actions__pending-card sa-skeleton" />
        </div>
        <template v-else-if="latestStatement">
          <div class="sa-section-hd">
            <h2 id="dashboard-statement-heading" class="sa-section-title">
              {{
                $t('Dashboard.Home.QuickActions.StatementTitle', {
                  year: taxYearLabel,
                })
              }}
            </h2>
          </div>
          <div class="sa-quick-actions__card">
            <div class="sa-quick-actions__summary">
              <span class="sa-quick-actions__figures">
                <span class="sa-quick-actions__amount">
                  {{ formatMoneyFromPence(latestStatement.totalDonated) }}
                </span>
                <span class="sa-quick-actions__meta">
                  {{ $t('Dashboard.Home.QuickActions.StatementTotal') }}
                </span>
              </span>
              <router-link
                :to="{ name: 'Statements' }"
                class="sa-quick-actions__cta"
                data-testid="dashboard-quick-action-statement"
              >
                {{ $t('Dashboard.Home.QuickActions.ViewStatement') }}
              </router-link>
            </div>
            <div
              class="sa-quick-actions__claim-reveal"
              :class="{ 'is-open': footerOpen && Boolean(claimAmount) }"
            >
              <div class="sa-quick-actions__claim-clip">
                <div
                  v-if="claimAmount"
                  class="sa-quick-actions__claim"
                  :inert="footerOpen ? undefined : true"
                >
                  <span class="sa-quick-actions__claim-copy">
                    <span class="sa-quick-actions__claim-lead">
                      {{ $t('Dashboard.Home.QuickActions.ClaimLead') }}
                    </span>
                    <span class="sa-quick-actions__claim-amount">
                      <span class="sa-quick-actions__claim-qual">{{
                        $t('Dashboard.ClaimTaxBack.UpToLabel')
                      }}</span>
                      {{ claimAmount }}
                    </span>
                    <span class="sa-quick-actions__claim-note">
                      {{ $t('Dashboard.Home.QuickActions.ClaimNote') }}
                    </span>
                  </span>
                  <router-link
                    :to="{ name: 'ClaimTaxBack' }"
                    class="sa-quick-actions__cta"
                    data-testid="dashboard-statement-claim"
                  >
                    {{ $t('Dashboard.Home.QuickActions.ClaimCta') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>
